import { Download } from "@/icons";
import clsx from "clsx";
import { ElementType, ReactNode, useState } from "react";
import { ArrowLink } from "../ArrowLink";
import { Button } from "../Button";
import { Col } from "../Col";
import "./OperatorCardDocument.scss";
import { saveAs } from "file-saver";
import { Link } from "@anoki/fse-common";
import { Row } from "../Row";
import { Divider } from "../Divider";

export type OperatorCardDocumentProps = {
  title: string;
  issuedAt: string;
  topRightContent?: ReactNode;
  details: {
    label: string;
    value: string;
  }[];
  downloadAction:
    | {
        title?: string;
        onDownload: () => void | Promise<void>;
        link?: Link;
      }
    | {
        title?: string;
        link: Link;
        onDownload?: () => void | Promise<void>;
      };
  openAction: {
    title: string;
    link: string;
    asLink?: "a" | ElementType;
  };
  className?: string;
};

export function OperatorCardDocument({
  title,
  issuedAt,
  details,
  downloadAction,
  openAction,
  className,
  topRightContent,
}: OperatorCardDocumentProps) {
  const LinkComponent = openAction.asLink ?? "a";

  const [isLoading, setIsLoading] = useState(false);
  const handleDownload = async () => {
    try {
      setIsLoading(true);
      if (downloadAction.onDownload) {
        await downloadAction.onDownload();
      } else if (downloadAction.link) {
        const response = await fetch(downloadAction.link.link);
        const blob = await response.blob();
        saveAs(blob, title);
      }
    } catch (error) {
      console.error("Error downloading file:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={clsx(
        "ui-operator-card-document shadow rounded bg-white",
        className,
      )}
    >
      <div className="ui-operator-card-content p-x24 ">
        <Row
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap="x16"
          mb="x16"
        >
          <p className="issued-at fs-7">{issuedAt}</p>
          {topRightContent}
        </Row>
        <h3 className="document-title fs-5 mb-x24 cursor-pointer hover-underline">
          <LinkComponent className="link-unstyled" href={openAction.link}>
            {title}
          </LinkComponent>
        </h3>
        <div className="metadata-grid">
          {details.map((detail, index) => (
            <Col key={index} className="metadata-item">
              <span className="metadata-label fs-6">{detail.label}</span>
              <span className="metadata-value fs-6">{detail.value}</span>
            </Col>
          ))}
        </div>
      </div>
      <div className="ui-operator-card-footer px-x24 py-x16 flex-wrap gap-x16">
        <Button
          className="bg-transparent download-button fs-7 fw-semibold"
          onClick={handleDownload}
          loading={isLoading}
        >
          <Download />
          {downloadAction.title ?? downloadAction.link?.title ?? "Download"}
        </Button>
        <Divider bg="border-divider" className="ui-mobile-divider" />
        <ArrowLink
          href={openAction.link}
          asLink={LinkComponent}
          className="fs-7 fw-semibold text-uppercase text-primary text-decoration-none"
        >
          {openAction.title}
        </ArrowLink>
      </div>
    </div>
  );
}
